import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * The few shapes every learning page shares (DESIGN.md): pill buttons, the
 * page title, the lime-disc empty state and the soft loading block. Colours
 * are theme names only, so each one follows the light and night palettes.
 */

/** The ONE main action on a screen: teal in light, lime in night. */
export const PILL_PRIMARY = 'inline-flex h-11 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground transition hover:bg-primary-dark disabled:opacity-50';
/** A secondary action: lime with ink. */
export const PILL_LIME = 'inline-flex h-11 items-center justify-center gap-2 rounded-full bg-lime px-6 text-sm font-semibold text-accent-foreground transition hover:brightness-95 disabled:opacity-50';
/** A quiet action on a card. */
export const PILL_QUIET = 'inline-flex h-11 items-center justify-center gap-2 rounded-full bg-surface px-6 text-sm font-semibold text-ink transition hover:bg-hair disabled:opacity-50';

export function PageHeader({
  eyebrow, title, subtitle, aside, titleTestId,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  aside?: ReactNode;
  titleTestId?: string;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow ? <p className="text-sm text-helper">{eyebrow}</p> : null}
        <h1 data-testid={titleTestId} className="text-[28px] font-semibold leading-tight text-ink">{title}</h1>
        {subtitle ? <p className="mt-1 text-[15px] text-helper">{subtitle}</p> : null}
      </div>
      {aside}
    </header>
  );
}

export function LearningEmpty({
  icon: Icon, title, body, action, testId, className,
}: {
  icon: LucideIcon;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  testId?: string;
  className?: string;
}) {
  return (
    <div
      data-testid={testId}
      className={cn('card-soft mx-auto flex w-full max-w-xl flex-col items-center gap-3 px-6 py-10 text-center', className)}
    >
      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-lime text-accent-foreground">
        <Icon className="h-7 w-7" aria-hidden />
      </span>
      <p className="text-lg font-semibold text-ink">{title}</p>
      {body ? <p className="max-w-sm text-sm text-helper">{body}</p> : null}
      {action ? <div className="pt-1">{action}</div> : null}
    </div>
  );
}

export function LoadingBlock({ testId }: { testId: string }) {
  return <div data-testid={testId} className="card-soft mx-auto h-40 w-full max-w-5xl animate-pulse" />;
}
